import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import { User } from '../../types';
import { PermissionGroup, permissionGroupsService } from '../../services/api/permissionGroups';
import { usersService } from '../../services/api/users';
import { useToast } from '../../contexts/ToastContext';

interface UserAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  group: PermissionGroup | null;
}

const UserAssignmentModal: React.FC<UserAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  group,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  const loadData = useCallback(async () => {
    if (!group) return;

    setLoading(true);
    try {
      const [groupUsers, usersResponse] = await Promise.all([
        permissionGroupsService.getGroupUsers(group.id),
        usersService.getAll({ pageSize: 1000, status: 'active' }),
      ]);

      setAssignedUsers(groupUsers);
      setAllUsers(usersResponse.items);
    } catch (err) {
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [group]);

  useEffect(() => {
    if (isOpen && group) {
      loadData();
      setSearchQuery('');
      setSelectedUserIds(new Set());
    }
  }, [isOpen, group, loadData]);

  const handleRemoveUser = async (userId: number) => {
    if (!group) return;

    try {
      // Ensure ID is a number (MySQL bigint returns as string)
      await permissionGroupsService.removeUsers(group.id, [Number(userId)]);
      showSuccess('User removed from group');
      await loadData();
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove user';
      showError(errorMessage);
    }
  };

  const handleAddSelectedUsers = async () => {
    if (!group || selectedUserIds.size === 0) return;

    try {
      // Ensure IDs are numbers (MySQL bigint returns as string)
      const userIdsArray = Array.from(selectedUserIds).map((id) => Number(id));
      await permissionGroupsService.assignUsers(group.id, userIdsArray);
      showSuccess(`${selectedUserIds.size} user(s) added to group`);
      setSelectedUserIds(new Set());
      await loadData();
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add users';
      showError(errorMessage);
    }
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Get available users (not already assigned)
  const assignedUserIds = new Set(assignedUsers.map((u) => u.id));
  const availableUsers = allUsers.filter(
    (user) =>
      !assignedUserIds.has(user.id) &&
      (searchQuery === '' ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!group) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('permissionGroups.manageUsers', 'Manage Users')}: ${group.name}`}
      size="large"
    >
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>{t('common.loading')}</div>
      ) : (
        <div>
          {/* Assigned Users Section */}
          <div className="users-section">
            <h3>{t('permissionGroups.assignedUsers', 'Assigned Users')} ({assignedUsers.length})</h3>
            
            {assignedUsers.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No users assigned to this group
              </div>
            ) : (
              <div className="user-list">
                {assignedUsers.map((user) => (
                  <div key={user.id} className="user-item">
                    <div className="user-info">
                      <div className="user-email">{user.email}</div>
                      <div className="user-role">{t(`roles.${user.role.toLowerCase()}`)}</div>
                    </div>
                    <button
                      className="user-remove"
                      onClick={() => handleRemoveUser(user.id)}
                      title="Remove from group"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Users Section */}
          <div className="add-users-section">
            <h4>{t('permissionGroups.addUsers', 'Add Users to Group')}</h4>
            
            <div className="user-search">
              <input
                type="text"
                placeholder={t('common.search', 'Search') + '...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                className="button button-primary button-small"
                onClick={handleAddSelectedUsers}
                disabled={selectedUserIds.size === 0}
              >
                {t('permissionGroups.addSelected', 'Add Selected')} ({selectedUserIds.size})
              </button>
            </div>

            <div className="available-users">
              {availableUsers.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  {searchQuery ? 'No matching users found' : 'All users are already assigned'}
                </div>
              ) : (
                availableUsers.slice(0, 50).map((user) => (
                  <div
                    key={user.id}
                    className={`available-user-item ${selectedUserIds.has(user.id) ? 'selected' : ''}`}
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="user-info">
                      <div className="user-email">{user.email}</div>
                      <div className="user-role">{t(`roles.${user.role.toLowerCase()}`)}</div>
                    </div>
                  </div>
                ))
              )}
              {availableUsers.length > 50 && (
                <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                  Showing first 50 users. Use search to find more.
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="button button-secondary" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserAssignmentModal;
